import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { useMemo } from 'react';
import { useAsync } from 'react-use';
import { batchEditorExecutePermission } from './batchEditor';

const ADMIN_GROUP_REFS = new Set(['group:default/admins', 'group:admins']);

function isAdminFromOwnershipRefs(ownershipEntityRefs: string[] | undefined): boolean {
  return (ownershipEntityRefs ?? []).some(ref => ADMIN_GROUP_REFS.has(ref));
}

/**
 * Mirrors batch-editor admin access: permission policy plus ownership refs on the
 * signed-in identity (covers cases where the permission round-trip is slow or
 * misconfigured in local dev).
 */
export function useBatchEditorAdmin() {
  const identityApi = useApi(identityApiRef);
  const { allowed: canExecute, loading: executeLoading } = usePermission({
    permission: batchEditorExecutePermission,
  });
  const identity = useAsync(() => identityApi.getBackstageIdentity(), [identityApi]);

  const ownershipAllowed = useMemo(
    () => isAdminFromOwnershipRefs(identity.value?.ownershipEntityRefs),
    [identity.value?.ownershipEntityRefs],
  );

  return {
    // Batch mapping endpoints require execute; use-only access must not show admin actions.
    allowed: canExecute || ownershipAllowed,
    loading: executeLoading || identity.loading,
  };
}
